// eslint-disable-next-line check-file/filename-naming-convention
import { faker } from "@faker-js/faker";
import { format } from "date-fns";
import noop from "lodash/noop";

import { fakeBaseUserType } from "@/__mocks__/mockBaseTypes";
import { merge } from "@/__mocks__/mockHelpers";
import { fakeUserTab } from "@/__mocks__/user";
import { SMTAnalyticsSource } from "@/constants/analytics/Analytics";
import { DateFormat } from "@/constants/DateFormat";
import {
  SMTEntryType,
  SMTFoodTypeValue,
  SMTPermissionTypeValue,
} from "@/constants/SMT";
import type { SupportPatientSMTPermissions } from "@/store/api/smt";
import type { AllSMTDailyEntriesModalProps } from "@/ui/components/smt/ViewSMTEntries/AllSMTDailyEntriesModal";
import type {
  SMTEntryDetails,
  SMTEntryProps,
} from "@/ui/components/smt/ViewSMTEntries/SMTEntry";
import type { SMTSingleDayEntriesAccordionProps } from "@/ui/components/smt/ViewSMTEntries/SMTSingleDayEntriesAccordion";
import { toSentenceCase } from "@/utils/string.util";

const fakeSMTFoodType: () => SMTFoodType = () =>
  faker.helpers.arrayElement(Object.values(SMTFoodTypeValue));

const fakeSMTEntryType: () => SMTEntryType = () =>
  faker.helpers.arrayElement(Object.values(SMTEntryType));

const fakeSMTPermissionType: () => SMTPermissionType = () =>
  faker.helpers.arrayElement(Object.values(SMTPermissionTypeValue));

const fakeSupportPatientSMTPermissions = (
  overrides?: DeepPartial<SupportPatientSMTPermissions>,
): SupportPatientSMTPermissions => {
  const props: SupportPatientSMTPermissions = {
    patientId: faker.string.uuid(),
    permissionType: fakeSMTPermissionType(),
  };
  return merge(props, overrides);
};

const fakeSMTEntryDetailsForFood = (
  overrides?: DeepPartial<SMTEntryDetails>,
): DeepRequired<SMTEntryDetails> => {
  const props: SMTEntryDetails = {
    entryDetailsProps: {
      foodComment: `${toSentenceCase(faker.word.words({ count: { max: 55, min: 30 } }))}.`,
      foodDescription: `${toSentenceCase(faker.word.words({ count: { max: 55, min: 30 } }))}.`,
      foodType: fakeSMTFoodType(),
      isNewFood: faker.datatype.boolean(),
    },
    entryType: SMTEntryType.Meal,
  };
  return merge(props, overrides);
};

const fakeSMTEntryProps = (
  overrides?: DeepPartial<SMTEntryProps>,
): DeepRequired<SMTEntryProps> => {
  // reuse same photo for thumbnail + full screen image, for consistency
  const sharedPhotoUrl = faker.image.urlLoremFlickr({ category: "food" });

  const props: SMTEntryProps = {
    avatarUserId: faker.string.uuid(),
    avatarUserName: faker.person.fullName(),
    avatarUserType: fakeBaseUserType(),
    details: fakeSMTEntryDetailsForFood(),
    photoUrl: sharedPhotoUrl,
    smtEntryId: faker.string.uuid(),
    thumbnailUrl: sharedPhotoUrl,
    time: format(faker.date.anytime(), "h:mma").toLowerCase(),
  };
  return merge(props, overrides);
};

const fakeSMTSingleDayEntriesAccordionProps = (
  overrides?: DeepPartial<SMTSingleDayEntriesAccordionProps>,
): DeepRequired<SMTSingleDayEntriesAccordionProps> => {
  const props: SMTSingleDayEntriesAccordionProps = {
    analyticsSource: faker.helpers.arrayElement(
      Object.values(SMTAnalyticsSource),
    ),
    dailyLogId: faker.string.uuid(),
    date: faker.date.anytime(),
    entries: faker.helpers.multiple(() => fakeSMTEntryProps(), {
      count: { max: 6, min: 2 },
    }),
    isOpen: faker.datatype.boolean(),
    onTriggerPress: noop,
    testID: faker.word.noun(),
  };
  return merge(props, overrides);
};

const fakeAllSMTDailyEntriesModalProps = (
  overrides?: DeepPartial<AllSMTDailyEntriesModalProps>,
): DeepRequired<AllSMTDailyEntriesModalProps> => {
  const props: AllSMTDailyEntriesModalProps = {
    accordionPropsList: faker.helpers.multiple(
      () => fakeSMTSingleDayEntriesAccordionProps(),
      { count: { max: 10, min: 2 } },
    ),
    analyticsSource: faker.helpers.arrayElement(
      Object.values(SMTAnalyticsSource),
    ),
    onUserChange: noop,
    testID: faker.word.noun(),
    userTabs: faker.helpers.multiple(() => fakeUserTab(), {
      count: { max: 10, min: 2 },
    }),
  };
  return merge(props, overrides);
};

const fakeSMTDailyLogImage = (
  overrides?: DeepPartial<SMTDailyLogImage>,
): DeepRequired<SMTDailyLogImage> => {
  const dailyLogImage: SMTDailyLogImage = {
    contentType: faker.word.words(),
    createdAt: faker.date.anytime().toISOString(),
    fileName: faker.system.fileName(),
    fileServiceKey: faker.word.words(),
    fileSize: faker.number.int(),
    id: faker.string.uuid(),
    thumbnailUrl: faker.image.urlLoremFlickr({ category: "food" }),
    url: faker.image.urlLoremFlickr({ category: "food" }),
  };
  return merge(dailyLogImage, overrides);
};

const fakeSMTDailyLogEntry = (
  overrides?: DeepPartial<SMTDailyLogEntry>,
): DeepRequired<SMTDailyLogEntry> => {
  // eslint-disable-next-line no-restricted-syntax -- >15 props is fine on a mock
  const dailyLogEntry: SMTDailyLogEntry = {
    calories: faker.number.int({ max: 10000, min: 1 }).toString(),
    comment: faker.word.words(),
    createdAt: faker.date.anytime().toISOString(),
    createdByUserFirstName: faker.person.firstName(),
    createdByUserId: faker.string.uuid(),
    createdByUserLastName: faker.person.lastName(),
    createdByUserType: fakeBaseUserType(),
    description: faker.word.words(),
    entryType: fakeSMTEntryType(),
    id: faker.string.uuid(),
    images: faker.helpers.multiple(() => fakeSMTDailyLogImage(), {
      count: { max: 10, min: 1 },
    }),
    isNewFood: faker.datatype.boolean(),
    mealId: faker.string.uuid(),
    mealType: fakeSMTFoodType(),
    // API returns occurredAt without timezone indicator (treated as UTC)
    occurredAt: faker.date.anytime().toISOString().replace("Z", ""),
    submittedBy: faker.string.uuid(),
    updatedAt: faker.date.anytime().toISOString(),
  };
  return merge(dailyLogEntry, overrides);
};

const fakeSMTDailyLog = (
  overrides?: DeepPartial<SMTDailyLog>,
): DeepRequired<SMTDailyLog> => {
  const dailyLog: SMTDailyLog = {
    id: faker.string.uuid(),
    logDate: format(faker.date.anytime(), DateFormat.yearMonthDay),
    smtEntries: faker.helpers.multiple(() => fakeSMTDailyLogEntry(), {
      count: { max: 10, min: 1 },
    }),
  };
  return merge(dailyLog, overrides);
};

export {
  fakeAllSMTDailyEntriesModalProps,
  fakeSMTDailyLog,
  fakeSMTDailyLogEntry,
  fakeSMTEntryDetailsForFood,
  fakeSMTEntryProps,
  fakeSMTEntryType,
  fakeSMTSingleDayEntriesAccordionProps,
  fakeSupportPatientSMTPermissions,
};
